import { useState, useRef, useEffect } from "react";
import { Button } from "@justworkshr/milo-core";
import { FormField, Select, SelectOption } from "@justworkshr/milo-form";
import DialogHeader from "pages/international-contractor-profile-page/components/DialogHeader/DialogHeader";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useProfileContextData } from "../../../contexts/ProfileInfoContext";
import { useResendEorInvitationEmailMutation } from "types/generated/operations";
import { getEmployeeDisplayName } from "../../../utils";
import type { AlertState } from "pages/employer-of-record/invite/types";
import styles from "./ResendInvitePopup.module.css";

interface ResendInvitePopupProps {
  isOpen: boolean;
  onClose: () => void;
  setAlert: (alert: AlertState | undefined) => void;
}

export default function ResendInvitePopup({
  isOpen,
  onClose,
  setAlert,
}: ResendInvitePopupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { profileInfo, memberData } = useProfileContextData();
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [resendEorInvitationEmailMutation, { loading: isLoading }] =
    useResendEorInvitationEmailMutation();
  const popupRef = useRef<HTMLDivElement>(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Only show the edit email link if the member hasn't created their account yet
  const shouldShowEditEmailLink =
    memberData?.eorMember?.accountCreated === false;

  const workEmail = profileInfo?.eorEmployeeProfile.employment?.workEmail;
  const homeEmail = profileInfo?.eorEmployeeProfile.personalInfo?.email;

  const emailOptions = [];
  if (workEmail) {
    emailOptions.push({
      value: workEmail,
      label: `${workEmail} • ${t("work")}`,
    });
  }
  if (homeEmail) {
    emailOptions.push({
      value: homeEmail,
      label: `${homeEmail} • ${t("home")}`,
    });
  }

  const handleSubmit = async () => {
    if (!selectedEmail) return;

    const memberUuid = profileInfo?.eorEmployeeProfile.personalInfo?.memberUuid;

    if (!memberUuid) {
      throw Error("Member UUID not found");
    }

    try {
      const result = await resendEorInvitationEmailMutation({
        variables: {
          memberUuid: memberUuid,
          ...(selectedEmail && { sendTo: selectedEmail }),
        },
      });

      if (result.data) {
        const employeeName = getEmployeeDisplayName(
          profileInfo?.eorEmployeeProfile.personalInfo
        );
        setAlert({
          visible: true,
          message: t("Invite sent to {{name}} at {{email}}", {
            name: employeeName,
            email: selectedEmail,
          }),
          type: "additive",
        });
        onClose();
        setSelectedEmail(""); // Reset selection
      }
    } catch (error) {
      console.error("Error resending invitation:", error);
      setAlert({
        visible: true,
        message: t(
          "We weren't able to resend the invite. Please try again later."
        ),
        type: "destructive",
      });
    }
  };

  const handleClose = () => {
    setSelectedEmail(""); // Reset selection when closing
    onClose();
  };

  const handleEditEmailClick = () => {
    handleClose(); // Close the popup first
    navigate(`/employer-of-record/employee-profile/${id}/edit#contact`);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.dialogContainer} ref={popupRef}>
      <div className={styles.dialog}>
        <DialogHeader title={t("Resend invitation")} onClose={handleClose} />

        <div className={styles.dialogBody}>
          <FormField label={t("Email address")} required>
            <Select
              id="resend-invite-email-select"
              name="selectedEmail"
              value={selectedEmail}
              onChange={(event) => setSelectedEmail(event.target.value)}
              placeholder={
                selectedEmail ? undefined : t("Select email address")
              }
              disabled={isLoading}
              // TODO: put this inline styling in CSS
              // the milo styling conflicts with that though, and the field extends past the container
              style={{
                textTransform: "none",
                width: "100%",
                maxWidth: "100%",
                minWidth: "0",
                boxSizing: "border-box",
              }}
            >
              {emailOptions.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          </FormField>

          {shouldShowEditEmailLink && (
            <div className={styles.editEmailContainer}>
              {t("Email is incorrect?")}{" "}
              <button
                type="button"
                onClick={handleEditEmailClick}
                className={styles.editEmailButton}
              >
                {t("Click here to edit")}
              </button>
            </div>
          )}
        </div>

        <footer className={styles.dialogFooter}>
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedEmail || isLoading}
            loading={isLoading}
          >
            {t("Send invite")}
          </Button>
        </footer>
      </div>
    </div>
  );
}
